import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Breadcrumbs, Link as MuiLink, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ProductForm from '../../components/Products/ProductForm';
import { getData, postFileUpload } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import { MdAddShoppingCart, MdArrowBack } from "react-icons/md";

const initialFormState = {
    title: "",
    description: "",
    key_features: "",
    how_to_use: "",
    category_id: "",
    subcategory_id: "",
    upc: "",
    product_images: [],
    productVideos: [],
    unitType: "",
    unitValue: 0,
    unitName: "",
    status: "approved",
    isPublished: false,
    brand: "",
    ideal_for: "",
    applied_for: "",
    container_type: "",
    country_of_orgin: "",
    color: "",
    gst: "",
    hsn_code: "",
    codAvailable: false,
    tags: [],
    b2c_mrp: '',
    b2c_discountType: '',
    b2c_discountValue: '',
    b2c_sellingPrice: '',
    b2b_discountType: '',
    b2b_discountValue: '',
    b2b_sellingPrice: '',
    visibility: ['b2b', 'b2c'],
    isFree: false,
    thresholdAmount: '',
    autoAdd: false,
    minimumStockQuantity: '',
    length: '',
    width: '',
    height: '',
    dimensionUnit: '',
    weight: '',
    weightUnit: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    badge: '',
    is_featured: false,
    ingredients: '',
    skin_type: '',
    hair_type: '',
    fragrance_notes: '',
    is_vegan: false,
    cruelty_free: false,
    organic: false,
    shipping_class: '',
    return_policy: 0,
    manufacturerName: '',
    manufacturerAddress: '',
    manufacturingLicenseNumber: ''
};

const AddProduct = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState(initialFormState);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const fetchCategories = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('categories/list?limit=1000', null, token);
            setCategories(Array.isArray(response.categories) ? response.categories : []);
        } catch (error) {
            console.error('Fetch categories failed:', error);
        }
    };

    const fetchSubcategories = async (categoryId = form.category_id) => {
        if (!categoryId) {
            setSubcategories([]);
            return;
        }
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`subcategories/list?limit=1000&category_id=${categoryId}`, null, token);
            setSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
        } catch (error) {
            setSubcategories([]);
            console.error('Fetch subcategories failed:', error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchSubcategories();
    }, [form.category_id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let updatedForm = { ...form, [name]: value };

        const calculatePrice = (mrp, discountType, discountValue) => {
            const m = parseFloat(mrp) || 0;
            const d = parseFloat(discountValue) || 0;
            if (discountType === 'flat') return Math.max(0, m - d).toFixed(0);
            if (discountType === 'percentage') return Math.max(0, m - (m * Math.min(100, d) / 100)).toFixed(0);
            return m.toFixed(0);
        };

        if (['b2c_mrp', 'b2c_discountType', 'b2c_discountValue'].includes(name)) {
            updatedForm.b2c_sellingPrice = calculatePrice(updatedForm.b2c_mrp, updatedForm.b2c_discountType, updatedForm.b2c_discountValue);
        }
        if (['b2c_mrp', 'b2b_discountType', 'b2b_discountValue'].includes(name)) {
            updatedForm.b2b_sellingPrice = calculatePrice(updatedForm.b2c_mrp, updatedForm.b2b_discountType, updatedForm.b2b_discountValue);
        }

        if (name === 'category_id') updatedForm.subcategory_id = '';
        setForm(updatedForm);
    };

    const handleAddProduct = async () => {
        setSubmitting(true);
        try {
            const tokenValue = sessionStorage.getItem('token');
            const userId = sessionStorage.getItem('userId');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            const formData = new FormData();
            const dimensionFields = ['length', 'width', 'height', 'dimensionUnit', 'weight', 'weightUnit'];
            const numericFields = ['b2c_mrp', 'b2c_discountValue', 'b2c_sellingPrice', 'b2b_discountValue', 'b2b_sellingPrice', 'thresholdAmount', 'minimumStockQuantity', 'return_policy'];

            Object.entries(form).forEach(([key, value]) => {
                if (key === 'product_images' && Array.isArray(value)) {
                    if (value.length === 0) {
                        formData.append('product_images', '');
                        formData.append('productVideos', '');
                    } else {
                        value.forEach(img => {
                            if (img instanceof File) {
                                if (img.type.startsWith('video/')) formData.append('productVideos', img);
                                else formData.append('product_images', img);
                            } else if (typeof img === 'string' && img.trim() !== '') {
                                const isVideo = /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(img);
                                if (isVideo) formData.append('productVideos', img);
                                else formData.append('product_images', img);
                            }
                        });
                    }
                } else if (key === 'productVideos') {
                    // Handled via product_images merging/split
                } else if (dimensionFields.includes(key)) {
                    // handled separately below
                } else if (numericFields.includes(key)) {
                    formData.append(key, value === '' ? 0 : Number(value));
                } else if (key === 'tags' || key === 'visibility') {
                    if (Array.isArray(value)) formData.append(key, value.join(','));
                } else {
                    formData.append(key, value);
                }
            });

            // Dimensions structure
            const dimensions = {
                length: form.length ? Number(form.length) : undefined,
                width: form.width ? Number(form.width) : undefined,
                height: form.height ? Number(form.height) : undefined,
                unit: form.dimensionUnit || undefined,
                weight: form.weight ? Number(form.weight) : undefined,
                weightUnit: form.weightUnit || undefined
            };
            Object.keys(dimensions).forEach(k => dimensions[k] === undefined && delete dimensions[k]);
            if (Object.keys(dimensions).length > 0) {
                formData.set('dimensions', JSON.stringify(dimensions));
            }

            formData.append('createdBy', userId);

            await postFileUpload(PRODUCT_ROUTES.ADD_PRODUCT, formData, token);
            alert('Product added successfully!');
            navigate('/productmanagement');
        } catch (error) {
            console.error('Add product failed:', error);
            alert('Failed to add product: ' + (error.message || 'Server error'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Mulish, sans-serif' }}>
                        Add New Product
                    </Typography>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                        <MuiLink underline="hover" color="inherit" onClick={() => navigate('/productmanagement')} sx={{ cursor: 'pointer' }}>
                            Product Management
                        </MuiLink>
                        <Typography color="text.primary">Add Product</Typography>
                    </Breadcrumbs>
                </Box>
                <Button 
                    variant="outlined" 
                    startIcon={<MdArrowBack />} 
                    onClick={() => navigate('/productmanagement')}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                >
                    Back to List
                </Button>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #e5e7eb' }}>
                <ProductForm
                    form={form}
                    onFormChange={handleChange}
                    onCancel={() => navigate('/productmanagement')}
                    onSubmit={handleAddProduct}
                    categories={categories}
                    subcategories={subcategories}
                    isEdit={false}
                    formMode="edit"
                />
                {submitting && (
                    <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default AddProduct;
